import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import moment from "moment";

import { COLLECTIONS, FINAL_ROUTES, HEATMAP } from "../../consts/sentryIds";
import { DEFAULT_TIMEBAND } from "../../consts/strings";
import {
  DEFAULT_START_TIME,
  DURATION_OPTIONS,
  MAX_TIME,
  MIN_TIME,
} from "../../consts/timeSlider";
import { NON_DELIVERY_PLAN_TYPES } from "../../consts/planTypes";
import { palette } from "../../theme/palette";

import {
  collectionViewSelector,
  getEarliestPlannedTime,
  getMatrixProfileType,
  getRouteMatrix,
  getRegularRoutersByTimeBands,
  heatmapViewSelector,
  isEditOneModeSelector,
  isProfileSelectedSelector,
  isWaveEditModeSelector,
  selectedOverlaySelector,
  wavesSelector,
} from "../../reducers/matrixProfile/selectors";
import {
  getPositionForLeft,
  getValueLabelValue,
  timeSliderFormat,
} from "../../utils/timeSlider";
import {
  previousSelectedTimeBandSelector,
  selectedTimeBandSelector,
} from "../../reducers/ownApp/selectors";
import {
  setCollectionView,
  setHeatmapView,
} from "../../reducers/matrixProfile";
import {
  moveWave,
  applyWavesChanges,
  cancelWavesChanges,
} from "../../reducers/matrixProfile/thunkActions";
import { setSelectedTimeBand } from "../../reducers/ownApp";
import { toggleFinalRoutesMode } from "../../reducers/ownApp/thunkActions";

import { useTimeSlider } from "../../hooks/useTimeSlider";

import { View } from "../View";
import { CheckboxWithLabel } from "../CheckboxWithLabel";
import { Duration } from "./Duration";
import MoveWave from "../Modals/MoveWave/MoveWave";
import { Navigation } from "./Navigation";
import RemoveWaveError from "../Modals/RemoveWaveError/RemoveWaveError";
import RouteListPopover from "../RouteListPopover";
import { RoutesCounts } from "./RoutesCounts";
import { SetRemoveTimeButton } from "./SetRemoveTimeButton";
import { SliderComponent } from "./SliderComponent";
import { WavesControls } from "./WavesControls";

const useStyles = makeStyles({
  container: {
    backgroundColor: palette.translucentBlack,
    width: "100%",
    height: "100px",
    display: "flex",
    flexDirection: "row-reverse",
  },
  checkboxLabel: {
    color: palette.typography.white.solid,
    fontSize: "0.75rem",
    lineHeight: "1rem",
    marginRight: "0.25rem",
    cursor: "pointer",
    display: "inline",
  },
  disabled: {
    pointerEvents: "none",
    background: palette.black,
    opacity: "0.5",
  },
  left: {
    flex: 1,
  },
  right: {
    width: "200px",
    paddingLeft: "20px",
  },
});

export const TimeSlider = ({ disabled }) => {
  const styles = useStyles();
  const [value, setValue] = useState(0);
  const [routeListAnchorEl, setRouteListAnchorEl] = React.useState(null);
  const [waveSelected, setWaveSelected] = React.useState(null);
  const isEditOneMode = useSelector(isEditOneModeSelector);
  const planType = useSelector(getMatrixProfileType);
  const routeMatrixConfig = useSelector(getRouteMatrix);
  const earliestPlannedTime = useSelector(getEarliestPlannedTime);
  const selectedTimeBand = useSelector(selectedTimeBandSelector);
  const previousSelectedTimeBand = useSelector(
    previousSelectedTimeBandSelector
  );
  const heatmapView = useSelector(heatmapViewSelector);
  const selectedOverlay = useSelector(selectedOverlaySelector);
  const collectionsView = useSelector(collectionViewSelector);
  const routesByTimeBand = useSelector(getRegularRoutersByTimeBands);
  const waves = useSelector(wavesSelector);
  const isWaveEditMode = useSelector(isWaveEditModeSelector);
  const isProfileSelected = useSelector(isProfileSelectedSelector);
  const [newWaves, setNewWaves] = useState();
  const [waveMoveOpen, setWaveMoveOpen] = useState(false);
  const [tempWaveMove, setTempWaveMove] = useState();
  const [removeWaveErrorOpen, setRemoveWaveErrorOpen] = useState(false);
  const dispatch = useDispatch();

  const [marks, min, max, setStartTime, duration, setDuration] = useTimeSlider(
    DEFAULT_START_TIME,
    DURATION_OPTIONS[1]
  );

  useEffect(() => {
    !isWaveEditMode && setNewWaves();
  }, [isWaveEditMode]);

  useEffect(() => {
    if (!selectedTimeBand) return;

    const timeBand =
      selectedTimeBand === DEFAULT_TIMEBAND && previousSelectedTimeBand
        ? previousSelectedTimeBand
        : selectedTimeBand;
    const date = moment.utc(new Date(0));
    date.hours(timeBand.split(":")[0]);
    date.minutes(timeBand.split(":")[1]);
    const selectedTime = timeSliderFormat(date);
    if (selectedTime === value) return;

    setValue(selectedTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeBand]);

  useEffect(() => {
    if (earliestPlannedTime && !NON_DELIVERY_PLAN_TYPES.includes(planType)) {
      setStartTime((earliestPlannedTime - (duration / 2) * 100) / 100);
      dispatch(setSelectedTimeBand(getValueLabelValue(earliestPlannedTime)));
    } else {
      dispatch(setSelectedTimeBand(DEFAULT_TIMEBAND));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, earliestPlannedTime, setStartTime, planType]);

  const onChangeCommitted = useCallback(
    (value) => {
      const valueLabel = getValueLabelValue(value);
      dispatch(setSelectedTimeBand(valueLabel));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const resetSlider = useCallback(
    (value) => {
      setValue(value);
      onChangeCommitted(value);
    },
    [onChangeCommitted]
  );

  useEffect(() => {
    if (value !== selectedTimeBand) return;
    if (value > max) resetSlider(max);
    if (value < min) resetSlider(min);
  }, [min, max, value, resetSlider, selectedTimeBand]);

  const changeStartTime = (newValue) => {
    if (newValue < MIN_TIME || newValue + duration > MAX_TIME) return;

    if (value / 100 < newValue) resetSlider(newValue * 100);

    if (value / 100 > newValue + duration)
      resetSlider((newValue + duration) * 100);

    setStartTime(newValue);
  };

  const onChange = (_, value) => {
    setValue(value);
  };

  const onClickRouteCount = (event, value) => {
    setRouteListAnchorEl(event.currentTarget);
    setWaveSelected(value);
  };

  const getWaves = useCallback(() => {
    return (newWaves || waves).map((wave) => {
      if (!!!wave.waveTime) return wave;
      const date = moment(wave.newWaveTime || wave.waveTime, "HH:mm");
      const time = timeSliderFormat(date);
      const position = getPositionForLeft({
        min,
        duration,
        position: time,
      });
      if (position < 0 || position > 100) {
        const { waveTime, waveNo } = wave;
        return { waveTime, waveNo };
      }

      return {
        ...wave,
        currentWaveTime: wave.newWaveTime || wave.waveTime,
        position,
        time,
      };
    });
  }, [duration, min, newWaves, waves]);

  return (
    <>
      {(routeMatrixConfig || selectedOverlay) && (
        <View className={styles.container}>
          <div className={styles.right}>
            <Grid container>
              <Grid container item xs={7} justifyContent={"flex-start"}>
                <Duration
                  selected={duration}
                  onChange={setDuration}
                  options={DURATION_OPTIONS}
                  disabled={disabled}
                />
              </Grid>
              <Grid
                container
                item
                xs={5}
                justifyContent={"flex-end"}
                alignItems={"flex-end"}
              >
                {isProfileSelected && (
                  <WavesControls
                    newWaves={newWaves}
                    onCancel={() => {
                      dispatch(cancelWavesChanges());
                      setNewWaves();
                    }}
                    onApply={() => {
                      dispatch(applyWavesChanges(newWaves));
                      setNewWaves();
                    }}
                  />
                )}
              </Grid>
            </Grid>
            <Navigation
              onLeft={() => {
                changeStartTime(parseInt(min) / 100 - 1);
              }}
              onRight={() => {
                changeStartTime(parseInt(min) / 100 + 1);
              }}
              disabled={disabled}
            />
            {!isWaveEditMode && (
              <div>
                <CheckboxWithLabel
                  label={"Final Routes"}
                  disabled={isEditOneMode}
                  checked={selectedTimeBand === DEFAULT_TIMEBAND}
                  sentryId={FINAL_ROUTES}
                  onChange={() => {
                    dispatch(toggleFinalRoutesMode());
                  }}
                />
                <Typography
                  className={styles.checkboxLabel}
                  onClick={(event) => {
                    onClickRouteCount(event, "final");
                  }}
                >
                  ({routesByTimeBand["final"]?.length})
                </Typography>
              </div>
            )}
            <div>
              <CheckboxWithLabel
                label={"Collections"}
                checked={collectionsView}
                onChange={() => {
                  dispatch(setCollectionView(!collectionsView));
                }}
                sentryId={COLLECTIONS}
              />
              {selectedOverlay && (
                <CheckboxWithLabel
                  label={"Heatmap"}
                  checked={heatmapView}
                  onChange={() => {
                    dispatch(setHeatmapView(!heatmapView));
                  }}
                  sentryId={HEATMAP}
                />
              )}
            </div>
            {isWaveEditMode && (
              <SetRemoveTimeButton
                value={value}
                waves={getWaves()}
                onChange={(waves) => setNewWaves(waves)}
                onError={() => setRemoveWaveErrorOpen(true)}
              />
            )}
          </div>

          <div className={clsx(styles.left, disabled && styles.disabled)}>
            <RoutesCounts marks={marks} onClick={onClickRouteCount} />
            <SliderComponent
              hideLabel={selectedTimeBand === DEFAULT_TIMEBAND}
              defaultValue={0}
              marks={marks}
              min={min}
              max={max}
              onChange={onChange}
              onChangeCommitted={(_, value) => onChangeCommitted(value)}
              value={value}
              waves={getWaves()}
              duration={duration}
              onWaveMove={(waves) => {
                setTempWaveMove(waves);
                setWaveMoveOpen(true);
              }}
            />
          </div>
        </View>
      )}
      <RouteListPopover
        anchorEl={routeListAnchorEl}
        onClose={() => setRouteListAnchorEl(null)}
        waveSelected={waveSelected}
      />
      <MoveWave
        open={waveMoveOpen}
        onClose={() => setWaveMoveOpen(false)}
        onCancel={() => {
          setTempWaveMove();
          setWaveMoveOpen(false);
        }}
        onContinue={async () => {
          dispatch(moveWave(tempWaveMove));
          setNewWaves(tempWaveMove);
          setWaveMoveOpen(false);
        }}
      />
      <RemoveWaveError
        open={removeWaveErrorOpen}
        onClose={() => setRemoveWaveErrorOpen(false)}
      />
    </>
  );
};

export default TimeSlider;
